/**
 * Task 13 — the URL scheme for `/me/:userId` (Account settings → Subscriptions),
 * binding for every FE task in this plan. See the global constraints doc,
 * "URL scheme" section, for the full contract.
 *
 * `section`: outer Account page tabs. `scope`: `overall` | `personal` |
 * `<organizationId>`. `tab`: inner tabs of a personal/org scope (unused by
 * `overall`). Every other query param (`setup`, `checkout_session`, `onboarding`,
 * `purchase`, …) passes through untouched.
 */
export const ACCOUNT_SECTION_PARAM = 'section';
export const ACCOUNT_SCOPE_PARAM = 'scope';
export const ACCOUNT_TAB_PARAM = 'tab';

export type AccountSection = 'profile' | 'subscriptions';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type AccountBillingScope = 'overall' | 'personal' | (string & {});

export type AccountBillingTab =
  | 'overview'
  | 'subscriptions'
  | 'cards'
  | 'invoices'
  | 'payouts';

export const ACCOUNT_BILLING_TABS: readonly AccountBillingTab[] = [
  'overview',
  'subscriptions',
  'cards',
  'invoices',
  'payouts',
];

/**
 * `/me/<userId>?section=subscriptions&scope=<scope>[&tab=<tab>][&<extra>]`.
 * `extraSearch` keys never override `section`/`scope`/`tab` — those three are
 * always written by the params above.
 */
export function buildAccountBillingRoute({
  userId,
  scope,
  tab,
  extraSearch,
}: {
  userId: string;
  scope: AccountBillingScope;
  tab?: AccountBillingTab;
  extraSearch?: string | URLSearchParams;
}): string {
  const params = new URLSearchParams();
  params.set(ACCOUNT_SECTION_PARAM, 'subscriptions');
  params.set(ACCOUNT_SCOPE_PARAM, scope);
  if (tab !== undefined) {
    params.set(ACCOUNT_TAB_PARAM, tab);
  }

  const extra = new URLSearchParams(extraSearch);
  extra.delete(ACCOUNT_SECTION_PARAM);
  extra.delete(ACCOUNT_SCOPE_PARAM);
  extra.delete(ACCOUNT_TAB_PARAM);
  for (const [key, value] of extra.entries()) {
    params.set(key, value);
  }

  return `/me/${userId}?${params.toString()}`;
}

/**
 * Where an organization-billing CTA has to land FOR THIS ACTOR.
 *
 * A platform actor has no Subscriptions tab on the Account page at all — it is
 * never a member and never a payer (P1/P2), so the account page folds
 * `section=subscriptions` back to the profile tab and any CTA aimed there is a
 * dead end. The legacy, platform-only `/organizations/:id/finance` page is the
 * one that still renders an organization's plan for them, so that is where
 * their CTAs go; everyone else keeps the new route.
 *
 * One function rather than an `isPlatformUser` ternary at each CTA, because a
 * call site that forgets the branch fails silently — the user simply lands on
 * a page with nothing on it.
 *
 * The legacy page only knows `overview`/`subscriptions`/`cards`/`invoices`
 * (`payouts` was its own route and is gone); an unknown value falls back to its
 * first tab rather than erroring.
 */
export function buildOrgBillingRouteForActor({
  isPlatformUser,
  userId,
  organizationId,
  tab,
}: {
  // `currentUser.isPlatformUser` is a `MaybeBoolean` — an absent or not-yet
  // loaded user is NOT a platform actor and keeps the regular route
  isPlatformUser: boolean | null | undefined;
  userId: string;
  organizationId: string;
  tab: AccountBillingTab;
}): string {
  return isPlatformUser
    ? `/organizations/${organizationId}/finance?${ACCOUNT_TAB_PARAM}=${tab}`
    : buildAccountBillingRoute({ userId, scope: organizationId, tab });
}

/**
 * Legacy `/organizations/:id/finance?tab=` → the new inner tab. `'payouts'`
 * is a distinct legacy route (`/organizations/:id/payouts`) and is handled by
 * the caller before this function ever sees it — deliberately absent from
 * this map, not an oversight.
 *
 * Every other member of {@link ACCOUNT_BILLING_TABS} is mapped explicitly:
 * `'overview'`/`'cards'`/`'invoices'` are identities, `'packages'` (the old
 * label) and `'subscriptions'` (the current one, `FinancePage.tsx`'s own
 * `tab=subscriptions` and what the plans tab's `returnTo('subscriptions')`
 * bakes into live Stripe/PaySpot return URLs) both land on `'subscriptions'`.
 * `default` only ever sees a missing or genuinely unrecognized value.
 */
export function mapLegacyFinanceTab(
  tab: string | undefined,
): AccountBillingTab {
  switch (tab) {
    case 'overview':
      return 'overview';
    case 'packages':
    case 'subscriptions':
      return 'subscriptions';
    case 'cards':
      return 'cards';
    case 'invoices':
      return 'invoices';
    default:
      return 'overview';
  }
}
